import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { nsLayoutStyles } from '../../../ns-styles/index.js';
import NSJobComponent from '../job.js';
import JobSchema from '../../../models/job-schema.js';
import type { FieldValues, TypeUi } from '../../../models/company-data.js';
import { readJobData, readLinkedRecords, saveDataRecord, type JobData } from '../../../helpers/company-data.js';
import { dataFields, type DataField } from '../../../views/data/definition.js';
import { hiddenReferenceHelp, jobFormValues, jobReferenceOptions } from '../../../views/data/job-values.js';
import '../../../ns-elements/ns-data-record-form.js';
import '../../../ns-elements/ns-loading.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/data-edit-record.json';
import { jobSchema } from '../job-schema.js';

// Change the mapped fields of the one record the job is about, through the job's own access
@customElement(NSDataEditRecord.is)
export default class NSDataEditRecord extends NSJobComponent {
  static get is() {
    return 'ns-data-edit-record';
  }

  @state()
  private __record?: JobData['records'][number];

  @state()
  private __fields: DataField[] = [];

  @state()
  private __values: FieldValues = {};

  // The records its references pick from, and the titles of those it points at now
  @state()
  private __linked?: Awaited<ReturnType<typeof readLinkedRecords>>;

  private __titles = new Map<string, string>();

  @state()
  private __edited = false;

  @state()
  private __stale = false;

  @state()
  private __error = '';

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { saved: { type: 'hidden' } } });
  }

  static styles = [
    nsLayoutStyles,
    ...super.styles,
    css`
      .content {
        display: flex;
        flex-direction: column;
        gap: var(--wa-space-m);
        height: 100%;
      }

      .prompt {
        white-space: pre-wrap;
      }

      .error {
        color: var(--wa-color-danger-on-quiet);
      }
    `,
  ];

  async connectedCallback() {
    await super.connectedCallback();
    await this.__read();
    // Someone else's save mustn't overwrite what's being typed, so an edited form is only marked out of date
    this._subscriptions.subscribe('dataRecord.*', (cr: { path?: string }) => {
      if (cr.path?.split('.')[1] !== this.__recordId) return;
      if (this.__edited) this.__stale = true;
      else this.__read();
    });
  }

  private get __recordId() {
    const id = this.job.inputValues?.RECORD;
    return typeof id === 'string' ? id : undefined;
  }

  private async __read() {
    try {
      const data = await readJobData(this._auth?.token, this.job.id, { recordId: this.__recordId });
      const fields = dataFields(data.definition, { ...data.ui, titleField: data.ui.titleField ?? '' } as TypeUi);
      this.__record = data.records[0];
      const { values, titles, hidden } = jobFormValues(fields, this.__record?.values ?? {});
      this.__fields = hiddenReferenceHelp(fields, hidden);
      this.__values = values;
      this.__titles = titles;
      this.__stale = false;
      // Workflow studio publishes a mapped reference only with a linked set to pick from
      if (!this.__linked && fields.some((field) => field.kind === 'reference')) {
        this.__linked = await readLinkedRecords(this._auth?.token, this.job.id);
      }
    } catch (err) {
      this.__error = err instanceof Error ? err.message : 'Unable to read the record for this job.';
    }
  }

  private async __save() {
    if (!this.__record) return;
    this.__error = '';
    try {
      this.loading('Saving the record');
      await saveDataRecord(this._auth?.token, {
        jobId: this.job.id,
        recordId: this.__record.id,
        revision: this.__record.revision,
        fields: this.__values,
      });
      await this.outcome('saved', {});
      this.loaded();
    } catch (err) {
      this.loaded();
      this.__error = err instanceof Error ? err.message : 'Unable to save the record.';
    }
  }

  renderActions() {
    if (!this.__record) return nothing;
    return html`
      <wa-button variant="brand" type="button" @click=${() => this.__save()}>Save and finish</wa-button>
    `;
  }

  render() {
    const prompt = this.job.inputValues?.PROMPT;
    if (!this.__record) {
      return this.__error
        ? html`
            <p class="error" role="alert">${this.__error}</p>
          `
        : html`
            <ns-loading label="Loading the record..."></ns-loading>
          `;
    }
    return html`
      <div class="content">
        ${typeof prompt === 'string' && prompt
          ? html`
              <p class="prompt">${prompt}</p>
            `
          : nothing}
        ${this.__record.title
          ? html`
              <h3>${this.__record.title}</h3>
            `
          : nothing}
        <ns-data-record-form
          .fields=${this.__fields}
          .values=${this.__values}
          .referenceOptions=${jobReferenceOptions(this.__fields, this.__linked, this.__values, this.__titles)}
          @values-change=${(e: CustomEvent<{ values: FieldValues }>) => {
            this.__values = e.detail.values;
            this.__edited = true;
          }}
        ></ns-data-record-form>
        ${this.__stale
          ? html`
              <p role="status">
                Someone else changed this record since you opened it.
                <wa-button size="small" appearance="plain" @click=${() => this.__read()}>Load their changes</wa-button>
              </p>
            `
          : nothing}
        ${this.__error
          ? html`
              <p class="error" role="alert">${this.__error}</p>
            `
          : nothing}
      </div>
    `;
  }
}
